import { apiv1 as api } from '@/adapters';
import type {
  RoleModel,
  TeamMemberModel,
  TeamModel,
  TeamRoleModel,
} from '@/adapters/apiv1.g';
import {
  extractAccepted,
  extractBoolean,
  extractPageModels,
  extractRole,
  extractTeam,
  extractTeamMember,
  extractTeamRole,
  unwrapData,
} from '@/adapters/shared';
import type { Role } from '@/types/core/role';
import type { Team, TeamMember, TeamRole } from '@/types/core/team';
import type { Page, PageRequest } from '@/types/shared/page';
import { toApiQuery } from '@/types/shared/page';
import type { UUID } from '@/utils/uuid';

const CONFLICT_RETRY_ATTEMPTS = 3;
const CONFLICT_RETRY_DELAY_MS = 400;

export const teamService = {
  /**
   * List teams and map API models to UI-friendly Team objects.
   */
  listTeams: async (
    tenantId: UUID,
    query?: PageRequest
  ): Promise<Page<Team>> => {
    const apiQuery = toApiQuery(query);
    const res = await api.listTeams(tenantId, apiQuery);
    const models = extractPageModels<TeamModel>(res);
    const next = res?.data?.next ?? null;
    const prev = res?.data?.prev ?? null;
    return { items: models.map(extractTeam), next, prev, limit: query?.limit };
  },

  /**
   * Find a single team by id.
   */
  findTeam: async (tenantId: UUID, teamId: UUID): Promise<Team | null> => {
    const res = await api.findTeam(tenantId, teamId);
    const model = unwrapData<TeamModel>(res);
    if (!model) return null;
    return extractTeam(model);
  },

  /**
   * Create or update a team.
   * The adapter uses a "reconcile" pattern (PUT) and returns an Accepted envelope.
   * Retries up to CONFLICT_RETRY_ATTEMPTS times with backoff when the backend returns
   * 409 Conflict (e.g. sequence mismatch / projector lag).
   */
  reconcileTeam: async (
    tenantId: UUID,
    teamId: UUID,
    body: { name: string; description?: string }
  ): Promise<boolean> => {
    const payload: { name: string; description: string } = {
      name: body.name,
      description: body.description ?? '',
    };
    let lastRes: Awaited<ReturnType<typeof api.reconcileTeam>> | null = null;
    for (let attempt = 0; attempt < CONFLICT_RETRY_ATTEMPTS; attempt++) {
      lastRes = await api.reconcileTeam(tenantId, teamId, payload);
      const status = (lastRes as { status?: number })?.status;
      if (status === 409 && attempt < CONFLICT_RETRY_ATTEMPTS - 1) {
        await new Promise((r) => setTimeout(r, CONFLICT_RETRY_DELAY_MS));
        continue;
      }
      break;
    }
    return extractAccepted(lastRes!);
  },

  /**
   * Create a new team.
   * Returns the teamId after successful reconciliation.
   */
  createTeam: async (
    tenantId: UUID,
    body: { name: string; description?: string }
  ): Promise<UUID> => {
    const teamId = crypto.randomUUID() as UUID;
    const success = await teamService.reconcileTeam(tenantId, teamId, body);
    if (!success) {
      throw new Error('Failed to create team');
    }
    return teamId;
  },

  /**
   * Update an existing team.
   */
  updateTeam: async (
    tenantId: UUID,
    teamId: UUID,
    body: { name: string; description?: string }
  ): Promise<void> => {
    const success = await teamService.reconcileTeam(tenantId, teamId, body);
    if (!success) {
      throw new Error('Failed to update team');
    }
  },

  /**
   * Delete/retire a team.
   */
  retireTeam: async (tenantId: UUID, teamId: UUID): Promise<boolean> => {
    const res = await api.removeTeam(tenantId, teamId);
    return extractBoolean(res);
  },

  /**
   * Delete a team (alias for retireTeam).
   */
  deleteTeam: async (tenantId: UUID, teamId: UUID): Promise<boolean> => {
    return teamService.retireTeam(tenantId, teamId);
  },

  /**
   * List members for a team and map API TeamMemberModel -> TeamMember
   */
  listTeamMembers: async (
    tenantId: UUID,
    teamId: UUID,
    query?: PageRequest
  ): Promise<Page<TeamMember>> => {
    const apiQuery = toApiQuery(query);
    const res = await api.listTeamMembers(tenantId, teamId, apiQuery);
    const models = extractPageModels<TeamMemberModel>(res);
    const next = res?.data?.next ?? null;
    const prev = res?.data?.prev ?? null;
    return {
      items: models.map(extractTeamMember),
      next,
      prev,
      limit: query?.limit,
    };
  },

  /**
   * Add/assign a member to a team.
   */
  assignTeamMember: async (
    tenantId: UUID,
    teamId: UUID,
    userId: UUID
  ): Promise<boolean> => {
    // Workaround: Send empty object instead of undefined to avoid backend EOF error
    const res = await api.assignTeamMember(tenantId, teamId, userId);
    return extractAccepted(res);
  },

  /**
   * Revoke a team member.
   */
  revokeTeamMember: async (
    tenantId: UUID,
    teamId: UUID,
    userId: UUID
  ): Promise<boolean> => {
    const res = await api.removeTeamMember(tenantId, teamId, userId);
    return extractBoolean(res);
  },

  /**
   * Find a specific member in a team.
   */
  findTeamMember: async (
    tenantId: UUID,
    teamId: UUID,
    userId: UUID
  ): Promise<TeamMember | null> => {
    const res = await api.findTeamMember(tenantId, teamId, userId);
    const model = unwrapData<TeamMemberModel>(res);
    if (!model) return null;
    return extractTeamMember(model);
  },

  /**
   * List roles assigned to a team (paged).
   */
  listTeamRoles: async (
    tenantId: UUID,
    teamId: UUID,
    query?: PageRequest
  ): Promise<Page<TeamRole>> => {
    const apiQuery = toApiQuery(query);
    const res = await api.listTeamRoles(tenantId, teamId, apiQuery);
    const models = extractPageModels<TeamRoleModel>(res);
    const next = res?.data?.next ?? null;
    const prev = res?.data?.prev ?? null;
    return {
      items: models.map(extractTeamRole),
      next,
      prev,
      limit: query?.limit,
    };
  },

  /**
   * Find a specific role assigned to a team.
   */
  findTeamRole: async (
    tenantId: UUID,
    teamId: UUID,
    roleId: UUID
  ): Promise<Role | null> => {
    const res = await api.findTeamRole(tenantId, teamId, roleId);
    const model = unwrapData<RoleModel>(res);
    return model ? extractRole(model) : null;
  },

  /**
   * Assign a role to a team.
   */
  assignTeamRole: async (
    tenantId: UUID,
    teamId: UUID,
    roleId: UUID
  ): Promise<boolean> => {
    const res = await api.assignTeamRole(tenantId, teamId, roleId);
    return extractAccepted(res);
  },

  /**
   * Revoke a role from a team.
   */
  revokeTeamRole: async (
    tenantId: UUID,
    teamId: UUID,
    roleId: UUID
  ): Promise<boolean> => {
    const res = await api.removeTeamRole(tenantId, teamId, roleId);
    return extractBoolean(res);
  },
};
